import { Autocomplete, Button, DialogContent, DialogTitle, ListItem, TextField, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ListWithLazyRender from "@entrilia/shared/components/ListWithLazyRender";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { DataCollectionRequestDetails } from "../../../../../api/types/dataCollectionTypes";

const addSubmissionsToDataRequest = withErrorHandling(adminApi.addSubmissionsToDataRequest);

interface Props {
  dataRequestId: string;
  assignedObjectIds: string[];
  onClose: () => void;
  onApply: (dataRequest: DataCollectionRequestDetails) => void;
}

const AddSubmissionsDialog = ({ dataRequestId, assignedObjectIds, onClose, onApply }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);

  const getObjects = useCallback(() => adminApi.getObjectsAsLookupReferences("PortfolioCompany"), []);

  const [objects, fetchError, { isFetching }] = useFetch(getObjects);

  const options = useMemo(
    () => (objects ?? []).filter((o) => !assignedObjectIds.includes(o.objectId)),
    [assignedObjectIds, objects]
  );

  const handleSubmit = async () => {
    setLoading(true);
    const [dataRequest, error] = await addSubmissionsToDataRequest(dataRequestId, {
      recipientObjectIds: selectedObjectIds,
    });
    setLoading(false);

    if (error) {
      logError(error, "[DeleteDataCollectionRequestDialog] addSubmissionsToDataRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Submissions created successfully");
    onApply(dataRequest);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Add Companies</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        {isFetching && <InlineLoader />}

        {fetchError && (
          <Typography variant="subtitle2" color="error">
            Failed to load available companies
          </Typography>
        )}

        {objects !== undefined && options.length === 0 && (
          <Typography variant="subtitle2" color="textSecondary">
            No companies available to add
          </Typography>
        )}

        {!isFetching && !fetchError && objects !== undefined && options.length > 0 && (
          <Autocomplete
            multiple
            fullWidth
            options={options}
            getOptionLabel={(o) => o.primaryFieldValue}
            onChange={(_, items) => items && setSelectedObjectIds(items.map((i) => i.objectId))}
            renderInput={(inputProps) => <TextField {...inputProps} autoFocus placeholder="Type company name..." />}
            renderOption={({ key: _key, ...props }, option) => (
              <ListItem key={option.objectId} {...props}>
                <Typography>{option.primaryFieldValue}</Typography>
              </ListItem>
            )}
            slotProps={{ listbox: { component: ListWithLazyRender } }}
          />
        )}
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleSubmit} disabled={selectedObjectIds.length === 0}>
          Add
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default AddSubmissionsDialog;
